/**
 * Computes a compact page-number window around the current page, e.g. `1 … 4 5 [6] 7 8 … 20`.
 * `null` entries render as an ellipsis. Kept dependency-free / pure so it's easy to unit test.
 */
export function paginationWindow(
  current: number,
  totalPages: number,
  siblingCount = 1,
): (number | null)[] {
  if (totalPages <= 1) return totalPages === 1 ? [1] : [];

  const totalNumbers = siblingCount * 2 + 5; // first + last + current + 2*siblings + 2 ellipses-worth
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(current - siblingCount, 1);
  const rightSibling = Math.min(current + siblingCount, totalPages);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  const pages: (number | null)[] = [1];

  if (showLeftEllipsis) {
    pages.push(null);
  } else {
    for (let p = 2; p < leftSibling; p++) pages.push(p);
  }

  for (let p = leftSibling; p <= rightSibling; p++) {
    if (p !== 1 && p !== totalPages) pages.push(p);
  }

  if (showRightEllipsis) {
    pages.push(null);
  } else {
    for (let p = rightSibling + 1; p < totalPages; p++) pages.push(p);
  }

  pages.push(totalPages);
  return pages;
}
